'use client';

import type { InboxPullRequest } from '../types';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useNow } from '@/hooks/use-now';
import { cn } from '@/lib/utils';
import { formatWithPattern, getFromNow } from '@/lib/utils/date-formatters';

const renderTimeRow = (label: string, date: Date) => (
  <div className="flex items-center justify-between gap-1">
    <span className="text-muted-foreground">{formatWithPattern(date, 'MMMM d, yyyy')}</span>
    <span className="font-mono tabular-nums text-zinc-400">
      {formatWithPattern(date, 'hh:mm:ss a')}
    </span>
    <span className="font-mono text-muted-foreground">{label}</span>
  </div>
);

export function PrUpdatedAt({
  createdAt,
  updatedAt,
  showAgo = true,
  className,
  side = 'left',
}: {
  createdAt: InboxPullRequest['createdAt'];
  updatedAt: InboxPullRequest['updatedAt'];
  showAgo?: boolean;
  className?: string;
  side?: 'left' | 'right';
}) {
  // Shared minute tick keeps the relative "x ago" strings fresh without a
  // per-instance interval (one timer total across all inbox rows).
  const now = useNow(60_000);

  // React Compiler memoizes each getFromNow call on its explicit date and tick.
  // getFromNow measures from live time, so prop changes between ticks stay fresh.
  // A closure that only read `now` (`void now`) was compiled away, which froze
  // these strings at each row's first render.
  const createdAtDateLocal = new Date(createdAt);
  const updatedAtDateLocal = new Date(updatedAt);

  // getTimezoneOffset returns minutes WEST of UTC, so negate it for the
  // GMT+/- label. Deriving the Date from the tick also consumes `now`, tying
  // the re-render to the shared ticker (and tracking DST flips while mounted).
  const localOffsetHours = -new Date(now).getTimezoneOffset() / 60;
  const localTzLabel = `GMT${localOffsetHours >= 0 ? '+' : ''}${localOffsetHours}`;

  return (
    <TooltipProvider delayDuration={50}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('min-w-6 cursor-default text-sm text-zinc-400', className)}>
            {getFromNow(updatedAt, { withoutSuffix: !showAgo, now })}
          </span>
        </TooltipTrigger>
        <TooltipContent side={side} className="min-w-[240px] space-y-2 text-xs">
          <div className="flex flex-col gap-1 mt-2">
            <span className="text-foreground">{`Created ${getFromNow(createdAt, { now })}`}</span>
            {renderTimeRow(localTzLabel, createdAtDateLocal)}
          </div>
          <div className="border-t border-muted/20" />
          <div className="flex flex-col gap-1">
            <span className="text-foreground">{`Updated ${getFromNow(updatedAt, { now })}`}</span>
            {renderTimeRow(localTzLabel, updatedAtDateLocal)}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
